import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  type SxProps,
  type Theme,
  Typography
} from '@mui/material';
import { grey } from '@mui/material/colors';
import type { WidgetProps } from 'app/schema-form/types';
import type React from 'react';
import { type ReactNode, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useWidgetTitle } from '../../../schema-form/util/widget.js';
import type { ImmutableMap } from '../../utils/immutable-types.js';
import InfoTooltip from '../info-tooltip/index.js';
import Instructions from '../instructions/index.js';

export const EnabledWidget = styled.div`
  display: block;
  width: 100%;
  margin-top: 0.5rem;
  margin-bottom: 1rem;

  &:not(:last-child) {
    padding: 0 0 8px 0;
  }

  .input {
    padding: 1.25rem 0 0.5rem;
  }

  // checkbox
  .field:only-of-type {
    margin-bottom: 0.5rem;
  }
`;

const Required = styled.span`
  position: relative;
  color: ${({ theme }): string => theme.palette.error.main} !important;
  font-size: 0.8em;
  top: -0.125rem;
`;

const ErrorText = styled.p`
  color: ${({ theme }): string => theme.palette.error.main};
  margin: 0.5rem 0;
`;

export type WidgetComponentProps = Partial<
  Pick<WidgetProps, 'schema' | 'config' | 'error' | 'isRequired' | 'disabled' | 'formName' | 'path'>
> & {
  readonly actions?: ReactNode;
  readonly innerRef?: ((instance: HTMLDivElement | null) => void) | React.RefObject<HTMLDivElement> | null | undefined;
  readonly className?: string;
  readonly children?: ReactNode;
  readonly testId?: string;
  readonly showHeader?: boolean;
  readonly showError?: boolean;
  readonly showDescription?: boolean;
};

export function getWidgetProps(props: Record<string, unknown> & WidgetComponentProps) {
  const { className, error, schema, config, isRequired, disabled, actions, children, innerRef } = props;
  return {
    className,
    error,
    schema,
    config,
    isRequired,
    disabled,
    actions,
    children,
    innerRef
  };
}

function WidgetTitle({ title, isRequired }: { readonly title: string; readonly isRequired?: boolean }) {
  return (
    <>
      {title}
      {isRequired && <Required> *</Required>}
    </>
  );
}

function WidgetActions({
  actions,
  schema,
  showDescription
}: Pick<WidgetComponentProps, 'actions' | 'schema' | 'showDescription'>) {
  const description = schema?.get('description');
  return (
    <Stack flexDirection="row" alignItems="center">
      {showDescription && description && <InfoTooltip top text={description} />}
      {actions}
    </Stack>
  );
}

type WidgetHeaderProps = Pick<
  WidgetComponentProps,
  'config' | 'actions' | 'isRequired' | 'schema' | 'showDescription' | 'showHeader' | 'path'
>;

function WidgetHeader(props: WidgetHeaderProps) {
  const { config, actions, isRequired, schema, showDescription = true, showHeader = true } = props;
  const instructions = config?.get('instructions');
  const description = schema?.get('description');
  const title = useWidgetTitle(props);

  const hasActions = Boolean(description ?? actions);
  const hasHeader = Boolean(showHeader && (instructions ?? title));

  if (!hasHeader && !hasActions) {
    return null;
  }

  return (
    <Stack flexDirection="row" alignItems="center">
      {hasHeader ? (
        <Box sx={{ flex: '1' }}>
          <Typography variant="h4" color="grey.700">
            <WidgetTitle title={title} isRequired={isRequired} />
          </Typography>
          <Typography variant="body2" color="grey.700">
            <Instructions value={instructions} />
          </Typography>
        </Box>
      ) : null}
      {hasActions ? (
        <Box>
          <WidgetActions schema={schema} actions={actions} showDescription={showDescription} />
        </Box>
      ) : null}
    </Stack>
  );
}

function WidgetChildren({
  children,
  error,
  showError = true
}: Pick<WidgetComponentProps, 'children' | 'error' | 'showError'>) {
  return (
    <Box>
      {children}
      {showError && error && <ErrorText>{error}</ErrorText>}
    </Box>
  );
}

function WidgetContents(props: WidgetComponentProps) {
  return (
    <>
      <WidgetHeader {...props} />
      <WidgetChildren {...props} />
    </>
  );
}

function WidgetWithCollapsibleContents(props: WidgetComponentProps) {
  const { config } = props;

  const isCollapsed = config?.get('isCollapsed') ?? true;
  const [expanded, setExpanded] = useState(!isCollapsed);

  const handleChange = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  return (
    <Accordion
      expanded={expanded}
      onChange={handleChange}
      sx={{
        border: `1px solid ${grey[400]}`
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <WidgetHeader {...props} showHeader />
      </AccordionSummary>
      <AccordionDetails>
        <WidgetChildren {...props} />
      </AccordionDetails>
    </Accordion>
  );
}

function Widget(props: WidgetComponentProps) {
  const { config, disabled, innerRef, testId } = props;
  const sx: ImmutableMap<SxProps<Theme>> | undefined = config?.get('sx');
  const isCollapsible = config?.get('isCollapsible');

  return (
    <Box
      ref={innerRef}
      data-testid={testId}
      sx={{
        display: 'inline-block',
        width: '100%',
        color: disabled ? 'rgba(0, 0, 0, 0.26)' : 'initial',
        ...(sx ? sx.toJS() : undefined)
      }}
    >
      {isCollapsible ? <WidgetWithCollapsibleContents {...props} /> : <WidgetContents {...props} />}
    </Box>
  );
}

export default Widget;
